import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";

/**
 * Shared button — single source of truth for the app.
 *
 * Matches the approved reference: solid NHS-blue primary with a soft
 * raised shadow, white-background / blue-outline / blue-text secondary,
 * generous padding and a ~10px radius. Brand colour comes from the
 * `--brand-primary` family token so it stays on-identity with +CURA.
 */
type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md";

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
};

const base =
  "inline-flex items-center justify-center gap-2 rounded-[10px] font-semibold " +
  "transition-all outline-none select-none whitespace-nowrap " +
  "focus-visible:ring-2 focus-visible:ring-[var(--border-focus)] focus-visible:ring-offset-2 " +
  "disabled:cursor-not-allowed disabled:opacity-55 disabled:shadow-none";

const sizes: Record<Size, string> = {
  sm: "h-9 px-3.5 text-[13px]",
  md: "h-11 px-5 text-[15px]",
};

const variants: Record<Variant, string> = {
  // Solid NHS-blue with a soft, slightly blue-tinted lift (per reference).
  primary:
    "border border-transparent bg-[var(--brand-primary)] text-white " +
    "shadow-[0_1px_2px_rgba(0,0,0,0.06),0_4px_12px_-3px_rgba(0,94,184,0.45)] " +
    "hover:bg-[var(--brand-primary-dark)] " +
    "hover:shadow-[0_1px_2px_rgba(0,0,0,0.08),0_6px_16px_-4px_rgba(0,94,184,0.50)] " +
    "active:bg-[var(--brand-primary-darker)] active:shadow-[0_1px_2px_rgba(0,0,0,0.10)]",
  // White, blue 1.5px outline, blue text/icon (per reference "Walk-in").
  secondary:
    "border-[1.5px] border-[var(--brand-primary)] bg-white text-[var(--brand-primary)] " +
    "hover:bg-[var(--brand-primary-wash)] active:bg-[var(--brand-primary-tint)]",
  // No chrome until interaction.
  ghost:
    "border border-transparent text-fg-2 hover:bg-[var(--bg-1)] hover:text-fg-1 active:bg-[var(--bg-2)]",
  // Destructive.
  danger:
    "border border-transparent bg-[var(--danger)] text-white shadow-sm " +
    "hover:opacity-90 active:opacity-100",
};

const Spinner = () => (
  <svg
    className="h-4 w-4 animate-spin"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
  >
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  {
    variant = "primary",
    size = "md",
    loading = false,
    leadingIcon,
    trailingIcon,
    disabled,
    className,
    children,
    type = "button",
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      // Excludes this button from brand.css's `[data-chrome] button`
      // reset (background/border/padding/font wipe). Without it, any
      // Button inside a data-chrome region — dialogs especially — renders
      // stripped of its styling because that rule out-specifies utilities.
      data-ui-button=""
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(base, sizes[size], variants[variant], className)}
      {...rest}
    >
      {loading ? <Spinner /> : leadingIcon}
      {children}
      {!loading && trailingIcon}
    </button>
  );
});
